import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import {
  ELMA365ComponentChangeModelOutput,
  ELMA365ToolsetModel,
  ELMA365VersionAction,
  ELMA365VersionAvailability,
  ELMA365VersionModel,
  ELMA365VersionPhase,
  ELMA365VersionStage,
  ELMA365VersionStatus,
} from '../../../../../../gamma-api';
import { defaultApi } from '../../../../../apis';
import { alertsSelectorAdd } from '../../../../../recoil';
import { getProblemDetails } from '../../../../../utils/errorUtils';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import AuthSession from '../../../../../components/AuthSession';
import { AuthSessionStatus, IAuthSessionState } from '../../../../../components/AuthSession/types';
import { isAuthChallenged, isAuthCompleted } from '../../../../../components/AuthSession/utils';
import StatusPanel from '../../../../../components/StatusPanel';

import ChangesModal from './ChangesModal';
import { EMPTY_MERGING_VERSION, TRANSLATION_GROUP_NAME } from './const';

interface IVersionMergeProps {
  toolset?: ELMA365ToolsetModel;
  visible: boolean;
  onFinish?: (version: ELMA365VersionModel) => void;
  onCancel?: () => void;
}

const VersionMerge: React.FC<IVersionMergeProps> = ({ toolset, visible, onFinish, onCancel }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [version, setVersion] = useState(EMPTY_MERGING_VERSION);
  const [changes, setChanges] = useState<ELMA365ComponentChangeModelOutput[]>();

  const [saving, setSaving] = useState(false);
  const [toggledKeys, setToggledKeys] = useState<{ [key: string]: boolean }>({});
  const [authState, setAuthState] = useState<IAuthSessionState>({ status: AuthSessionStatus.Unknown });

  const translate = getTranslateFunc(`${TRANSLATION_GROUP_NAME}:`);

  const createVersion = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (!toolset) {
      return newVersion;
    }

    if (newVersion.stage !== ELMA365VersionStage.Connector) {
      return newVersion;
    }

    const versionResponse = await defaultApi.createELMA365ToolkitVersion(
      {
        folder_id: toolset.folder_id,
        connector_id: toolset.connector_id,
        toolset_id: toolset.toolset_id,
        action: ELMA365VersionAction.Merging,
        availability: ELMA365VersionAvailability.Enabled,
        name: toolset.name,
      },
      authState.options
    );

    newVersion = {
      ...versionResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Namespaces,
      phase: ELMA365VersionPhase.Obtaining,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const obtainNamespaces = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Namespaces) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Obtaining) {
      return newVersion;
    }

    const namespaceResponse = await defaultApi.obtainELMA365ToolkitNamespaces(
      {
        version_id: newVersion.version_id,
      },
      authState.options
    );

    newVersion = {
      ...namespaceResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      phase: ELMA365VersionPhase.Selection,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const selectNamespaces = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Namespaces) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Selection) {
      return newVersion;
    }

    const namespaceResponse = await defaultApi.selectELMA365ToolkitNamespaces({
      version_id: newVersion.version_id,
      namespace_ids: {},
    });

    newVersion = {
      ...namespaceResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Applications,
      phase: ELMA365VersionPhase.Obtaining,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const obtainApplications = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Applications) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Obtaining) {
      return newVersion;
    }

    const applicationsResponse = await defaultApi.obtainELMA365ToolkitApplications(
      {
        version_id: newVersion.version_id,
      },
      authState.options
    );

    newVersion = {
      ...applicationsResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      phase: ELMA365VersionPhase.Selection,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const selectApplications = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Applications) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Selection) {
      return newVersion;
    }

    const applicationsResponse = await defaultApi.selectELMA365ToolkitApplications(
      {
        version_id: newVersion.version_id,
        application_ids: {},
      },
      authState.options
    );

    newVersion = {
      ...applicationsResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Processes,
      phase: ELMA365VersionPhase.Obtaining,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const obtainProcesses = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Processes) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Obtaining) {
      return newVersion;
    }

    const processesResponse = await defaultApi.obtainELMA365ToolkitProcesses(
      {
        version_id: newVersion.version_id,
      },
      authState.options
    );

    newVersion = {
      ...processesResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      phase: ELMA365VersionPhase.Selection,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const selectProcesses = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Processes) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Selection) {
      return newVersion;
    }

    const processesResponse = await defaultApi.selectELMA365ToolkitProcesses(
      {
        version_id: newVersion.version_id,
        process_ids: {},
      },
      authState.options
    );

    newVersion = {
      ...processesResponse.data.version,
      status: ELMA365VersionStatus.Pending,
      stage: ELMA365VersionStage.Tools,
      phase: ELMA365VersionPhase.Pending,
    };
    setVersion(newVersion);
    return newVersion;
  };

  const compareVersions = async (newVersion: ELMA365VersionModel) => {
    if (!toolset) {
      return newVersion;
    }

    const comparisonResponse = await defaultApi.compareELMA365ToolkitVersions(
      {
        old_version_id: toolset.version_id,
        new_version_id: newVersion.version_id,
      },
      authState.options
    );

    const changes: ELMA365ComponentChangeModelOutput[] = [];
    for (const change of comparisonResponse.data.changes) {
      changes.push(...change.items);
    }
    setChanges(changes);
  };

  const obtainTools = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Tools) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Pending) {
      return newVersion;
    }

    newVersion = {
      ...newVersion,
      status: ELMA365VersionStatus.Processing,
      phase: ELMA365VersionPhase.Obtaining,
    };
    setVersion(newVersion);

    const toolsResponse = await defaultApi.obtainELMA365ToolkitTools(
      {
        version_id: newVersion.version_id,
      },
      authState.options
    );

    newVersion = {
      ...toolsResponse.data.version,
      status: ELMA365VersionStatus.Processing,
      phase: ELMA365VersionPhase.Selection,
    };
    setVersion(newVersion);

    return newVersion;
  };

  const selectTools = async (newVersion: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    if (newVersion.stage !== ELMA365VersionStage.Tools) {
      return newVersion;
    }

    if (newVersion.phase !== ELMA365VersionPhase.Selection) {
      return newVersion;
    }

    const toolsResponse = await defaultApi.selectELMA365ToolkitTools(
      {
        version_id: newVersion.version_id,
        component_keys: toggledKeys,
        tool_ids: {},
      },
      authState.options
    );

    newVersion = toolsResponse.data.version;
    setVersion(newVersion);

    return newVersion;
  };

  const deleteVersion = async (newVersion: ELMA365VersionModel) => {
    await defaultApi.deleteELMA365ToolkitVersion(newVersion.version_id, authState.options);
  };

  const loadMerge = async (newVersion?: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    newVersion = newVersion || version;
    try {
      newVersion = await createVersion(newVersion);
      newVersion = await obtainNamespaces(newVersion);
      newVersion = await selectNamespaces(newVersion);
      newVersion = await obtainApplications(newVersion);
      newVersion = await selectApplications(newVersion);
      newVersion = await obtainProcesses(newVersion);
      newVersion = await selectProcesses(newVersion);
      await compareVersions(newVersion);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      const problemDetails = getProblemDetails(error);
      newVersion = {
        ...newVersion,
        status: ELMA365VersionStatus.Problem,
        problem: { details: problemDetails },
      };
      setVersion(newVersion);
    }
    return newVersion;
  };

  const finishMerge = async (newVersion?: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    newVersion = newVersion || version;

    if (!changes) {
      return newVersion;
    }

    setSaving(true);
    try {
      newVersion = await obtainTools(newVersion);
      newVersion = await selectTools(newVersion);
    } catch (error) {
      addAlert({
        content: translate('Failed to merge version'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(error);
      const problemDetails = getProblemDetails(error);
      newVersion = {
        ...newVersion,
        status: ELMA365VersionStatus.Problem,
        problem: { details: problemDetails },
      };
      setVersion(newVersion);
      setSaving(false);
      return newVersion;
    }
    addAlert({
      content: translate('Version has been merged'),
      type: 'success',
    });
    setSaving(false);
    onFinish?.(newVersion);
    return newVersion;
  };

  const cancelMerge = async (newVersion?: ELMA365VersionModel): Promise<ELMA365VersionModel> => {
    newVersion = newVersion || version;

    if (!newVersion.version_id) {
      onCancel?.();
      return newVersion;
    }

    setSaving(true);
    try {
      await deleteVersion(newVersion);
    } catch (error) {
      addAlert({
        content: translate('Failed to clear version'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setSaving(false);
      onCancel?.();
    }
    return newVersion;
  };

  const onVisiblePropChange = () => {
    if (!visible) {
      return;
    }

    setVersion(EMPTY_MERGING_VERSION);
    setChanges(undefined);
    setToggledKeys({});
    setAuthState({ status: AuthSessionStatus.Unknown });
  };

  useEffect(onVisiblePropChange, [visible]);

  const onAuthStateChange = () => {
    if (isAuthChallenged(authState)) {
      setAuthState({ ...authState, status: AuthSessionStatus.Redirection });
    } else if (isAuthCompleted(authState)) {
      loadMerge(version).finally();
    }
  };

  useEffect(onAuthStateChange, [authState]);

  const isDataLoading = (): boolean => {
    if (version.status === ELMA365VersionStatus.Problem) {
      return false;
    }

    return !changes;
  };

  const isDataEqual = (): boolean => {
    if (!changes) {
      return false;
    }

    for (const change of changes) {
      if (change.diffs.length) {
        return false;
      }
    }

    return true;
  };

  const isDataError = (): boolean => {
    return version.status === ELMA365VersionStatus.Problem;
  };

  const hasDataDiffs = (): boolean => {
    if (!changes) {
      return false;
    }

    for (const change of changes) {
      if (change.diffs.length) {
        return true;
      }
    }

    return false;
  };

  const onChangeToggle = (key: string, selected: boolean) => {
    setToggledKeys({ ...toggledKeys, [key]: selected });
  };

  const onMergeFinish = async () => {
    await finishMerge(version);
  };

  const onMergeCancel = async () => {
    await cancelMerge(version);
  };

  const onMergeRestart = async () => {
    const newVersion = {
      ...version,
      status: ELMA365VersionStatus.Processing,
      problem: null,
    };
    setVersion(newVersion);

    if (newVersion.stage === ELMA365VersionStage.Tools) {
      await finishMerge(newVersion);
    } else {
      await loadMerge(newVersion);
    }
  };

  const onAuthChange = async (newState: IAuthSessionState) => {
    setAuthState(newState);
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <StatusPanel.LoadingPanel
        group={TRANSLATION_GROUP_NAME}
        loading={saving}
        visible={isDataLoading()}
        onClose={onMergeCancel}
      />
      <StatusPanel.SuccessPanel
        group={TRANSLATION_GROUP_NAME}
        loading={saving}
        visible={isDataEqual()}
        onClose={onMergeCancel}
      />
      <StatusPanel.ErrorPanel
        group={TRANSLATION_GROUP_NAME}
        loading={saving}
        problem={version.problem}
        visible={isDataError()}
        onClose={onMergeCancel}
        onRestart={onMergeRestart}
      />
      <ChangesModal
        changes={changes}
        loading={saving}
        toggledKeys={toggledKeys}
        visible={hasDataDiffs()}
        onCancel={onMergeCancel}
        onFinish={onMergeFinish}
        onToggle={onChangeToggle}
      />
      <AuthSession connectorId={toolset?.connector_id} status={authState.status} onChange={onAuthChange} />
    </>
  );
};

export default VersionMerge;
